/**
 * Opening Ask Lilia in its own window (Olivia, 10 Oct, handoff-ask-lilia-window):
 * a new window at 420 × 760, or Keep on top at 380 × 600, never under 340 × 480,
 * each reopening at its last size (and, for a window, its last place).
 */

export type AskWindowKind = "window" | "top";

export interface Geometry {
  width: number;
  height: number;
  left?: number;
  top?: number;
}

export const DEFAULT_SIZE: Record<AskWindowKind, Geometry> = {
  window: { width: 420, height: 760 },
  top: { width: 380, height: 600 },
};
/** Below this the message box and the change card stop fitting. */
export const MIN_SIZE = { width: 340, height: 480 };

const KEY: Record<AskWindowKind, string> = { window: "lilia.ask.window", top: "lilia.ask.on-top" };

/** The size (and place) to open at: the last one, never under the minimum. */
export function savedGeometry(kind: AskWindowKind): Geometry {
  let g: Partial<Geometry> = {};
  try {
    g = JSON.parse(localStorage.getItem(KEY[kind]) || "{}") as Partial<Geometry>;
  } catch {
    g = {};
  }
  const base = DEFAULT_SIZE[kind];
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
  return {
    width: Math.max(MIN_SIZE.width, num(g.width) ?? base.width),
    height: Math.max(MIN_SIZE.height, num(g.height) ?? base.height),
    ...(num(g.left) !== undefined && num(g.top) !== undefined ? { left: g.left, top: g.top } : {}),
  };
}

export function saveGeometry(kind: AskWindowKind, g: Geometry): void {
  try {
    localStorage.setItem(KEY[kind], JSON.stringify(g));
  } catch {
    // Not kept; the next window opens at the default size.
  }
}

/** The Ask page for a document; `on=top` when it fills a Keep-on-top window. */
export function askPageUrl(documentId: string, kind: AskWindowKind): string {
  return `/d/${documentId}/ask${kind === "top" ? "?on=top" : ""}`;
}

/** The features string for window.open at a geometry. */
export function windowFeatures(g: Geometry): string {
  const parts = ["popup", `width=${Math.round(g.width)}`, `height=${Math.round(g.height)}`];
  if (g.left !== undefined && g.top !== undefined) parts.push(`left=${Math.round(g.left)}`, `top=${Math.round(g.top)}`);
  return parts.join(",");
}

/** A new browser window with Ask Lilia, or null when the browser blocked it. */
export function openAskWindow(documentId: string): Window | null {
  return window.open(askPageUrl(documentId, "window"), `lilia-ask-${documentId}`, windowFeatures(savedGeometry("window")));
}

interface DocumentPiP {
  requestWindow: (opts: { width: number; height: number }) => Promise<Window>;
}

/**
 * Keep on top (Document Picture-in-Picture, Chrome and Edge): an always-on-top
 * window holding the Ask page in a frame, so it is its own page, with its own
 * menus and styles. Must be called from a click.
 */
export async function openAskOnTop(documentId: string): Promise<Window | null> {
  const pipApi = (window as unknown as { documentPictureInPicture?: DocumentPiP }).documentPictureInPicture;
  if (!pipApi) return null;
  const g = savedGeometry("top");
  const pip = await pipApi.requestWindow({ width: g.width, height: g.height });
  const doc = pip.document;
  doc.title = "Ask Lilia";
  const style = doc.createElement("style");
  style.textContent = "html,body{margin:0;height:100%;overflow:hidden;background:#fff}iframe{display:block;width:100%;height:100%;border:0}";
  doc.head.append(style);
  const frame = doc.createElement("iframe");
  frame.src = new URL(askPageUrl(documentId, "top"), window.location.href).href;
  frame.title = "Ask Lilia";
  doc.body.append(frame);
  pip.addEventListener("pagehide", () => saveGeometry("top", { width: pip.innerWidth, height: pip.innerHeight }));
  return pip;
}
